import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { Link } from "react-router-dom";
import { ArrowLeft, ChevronDown, FileText, Receipt, Save, X } from "lucide-react";
import { toast } from "react-toastify";
import { api } from "../../../lib/axios";
import { A4PreviewShell, A4_WIDTH } from "../../../components/documents/A4PreviewShell";
import {
  DEFAULT_COMMERCIAL_CONFIG,
  DEFAULT_FORMAT_NUMERO,
  getLogoUrl,
  getDevise,
  getTauxTvaPercent,
  normalizeCommercialConfig,
  type CommercialConfig,
} from "../../../components/documents/commercialConfig";
import { InvoicePreview } from "../../../components/documents/InvoicePreview";
import { ProformaPreview } from "../../../components/documents/ProformaPreview";
import { QuittancePreview } from "../../../components/documents/QuittancePreview";

type TabKey = "facture" | "proforma" | "quittance";

const ZOOM_PRESETS: { label: string; value: number }[] = [
  { label: "60 %", value: 0.6 },
  { label: "70 %", value: 0.7 },
  { label: "80 %", value: 0.8 },
];

const FULLSCREEN_ZOOM = 0.92;

function useIsXl(): boolean {
  const [xl, setXl] = useState(false);
  useEffect(() => {
    const mq = window.matchMedia("(min-width: 1280px)");
    const apply = () => setXl(mq.matches);
    apply();
    mq.addEventListener("change", apply);
    return () => mq.removeEventListener("change", apply);
  }, []);
  return xl;
}

function AccordionSection({
  title,
  defaultOpen,
  children,
}: {
  title: string;
  defaultOpen?: boolean;
  children: React.ReactNode;
}): React.JSX.Element {
  return (
    <details
      className="group rounded-xl border border-slate-800 bg-slate-950/40 open:border-slate-700 open:bg-slate-900/40"
      open={defaultOpen}
    >
      <summary className="flex cursor-pointer list-none items-center justify-between gap-2 px-3 py-2.5 text-sm font-medium text-slate-200 [&::-webkit-details-marker]:hidden">
        <span>{title}</span>
        <ChevronDown className="h-4 w-4 shrink-0 text-slate-500 transition-transform group-open:rotate-180" />
      </summary>
      <div className="border-t border-slate-800/80 px-3 pb-3 pt-2">{children}</div>
    </details>
  );
}

const labelClass = "block text-xs font-medium text-slate-300";
const fieldInput =
  "mt-1.5 w-full rounded-lg border border-slate-700 bg-slate-950 px-3 py-2 text-sm text-white outline-none placeholder:text-slate-600 focus:border-emerald-500/60";

function buildConfigurationPayload(cfg: CommercialConfig): Record<string, unknown> {
  const nomCore =
    cfg.nomLegal?.trim() ||
    cfg.nomCommercial?.trim() ||
    cfg.nomEntreprise.trim() ||
    "EspoirAfriqueSas";
  const mergedLogo = getLogoUrl(cfg) || null;
  const dvs = getDevise(cfg);
  const tv = getTauxTvaPercent(cfg);

  const str = (s: string | null | undefined) => (s?.trim() ? s.trim() : null);
  const intOrNull = (n: number | null | undefined) =>
    n != null && Number.isFinite(n) ? Math.trunc(n) : null;

  return {
    nomEntreprise: nomCore,
    nomLegal: str(cfg.nomLegal),
    nomCommercial: str(cfg.nomCommercial),
    slogan: str(cfg.slogan),
    adresse: str(cfg.adresse),
    adresseComplete: str(cfg.adresseComplete),
    telephone: str(cfg.telephone),
    email: str(cfg.email),
    siteWeb: str(cfg.siteWeb),
    rccm: str(cfg.rccm),
    nif: str(cfg.nif),
    idNat: str(cfg.idNat),
    numeroImpotTva: str(cfg.numeroImpotTva),
    logo: mergedLogo,
    logoUrl: mergedLogo,
    devise: dvs,
    deviseDefaut: dvs,
    tauxTVA: tv,
    tauxTva: tv,
    tauxChange: cfg.tauxChange ?? 1,
    numerotationFacturePrefix: str(cfg.numerotationFacturePrefix) ?? "FAC",
    numerotationProformaPrefix: str(cfg.numerotationProformaPrefix) ?? "PRO",
    numerotationQuittancePrefix: str(cfg.numerotationQuittancePrefix) ?? "QUI",
    formatNumero: str(cfg.formatNumero) || DEFAULT_FORMAT_NUMERO,
    prochainNumeroFacture: intOrNull(cfg.prochainNumeroFacture) ?? 1,
    prochainNumeroProforma: intOrNull(cfg.prochainNumeroProforma) ?? 1,
    prochainNumeroQuittance: intOrNull(cfg.prochainNumeroQuittance) ?? 1,
    mentionTva: str(cfg.mentionTva),
    exonereTva: cfg.exonereTva === true,
    dateTauxChange: cfg.dateTauxChange?.trim()
      ? new Date(`${cfg.dateTauxChange.trim()}T12:00:00`).toISOString()
      : null,
    mentionFacture: str(cfg.mentionFacture),
    mentionProforma: str(cfg.mentionProforma),
    mentionQuittance: str(cfg.mentionQuittance),
    mentionsLegales: str(cfg.mentionsLegales),
    conditionsPaiement: str(cfg.conditionsPaiement),
    delaiPaiementJours: intOrNull(cfg.delaiPaiementJours),
    penaliteRetard: str(cfg.penaliteRetard),
    validiteProformaJours: intOrNull(cfg.validiteProformaJours),
    clauseContestation: str(cfg.clauseContestation),
    piedPageFacture: str(cfg.piedPageFacture),
    nomSignataire: str(cfg.nomSignataire),
    fonctionSignataire: str(cfg.fonctionSignataire),
    signatureCachet: str(cfg.signatureCachet),
    signatureCachetUrl: str(cfg.signatureCachetUrl),
    banque: str(cfg.banque),
    nomCompte: str(cfg.nomCompte),
    numeroCompte: str(cfg.numeroCompte),
    iban: str(cfg.iban),
    swift: str(cfg.swift),
    deviseCompte: str(cfg.deviseCompte),
    mobileMoneyEntreprise: str(cfg.mobileMoneyEntreprise),
  };
}

export default function DocumentsCommerciauxPage(): React.JSX.Element {
  const [tab, setTab] = useState<TabKey>("facture");
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [cfg, setCfg] = useState<CommercialConfig | null>(null);
  const [draftMode, setDraftMode] = useState(false);
  const [previewZoom, setPreviewZoom] = useState(0.68);
  const [fullscreenOpen, setFullscreenOpen] = useState(false);
  const previewWrapRef = useRef<HTMLDivElement>(null);
  const [containerW, setContainerW] = useState(0);
  const isXl = useIsXl();

  const recomputeW = useCallback(() => {
    const el = previewWrapRef.current;
    if (el) setContainerW(el.clientWidth);
  }, []);

  useEffect(() => {
    recomputeW();
  }, [recomputeW, tab, cfg, loading]);

  useEffect(() => {
    const el = previewWrapRef.current;
    if (!el || typeof ResizeObserver === "undefined") return;
    const ro = new ResizeObserver(() => recomputeW());
    ro.observe(el);
    window.addEventListener("resize", recomputeW);
    return () => {
      ro.disconnect();
      window.removeEventListener("resize", recomputeW);
    };
  }, [recomputeW]);

  const effectiveZoom = useMemo(() => {
    if (isXl) return Math.max(0.12, Math.min(1, previewZoom));
    if (containerW > 0) {
      const fit = (containerW - 24) / A4_WIDTH;
      return Math.max(0.32, Math.min(0.55, fit));
    }
    return 0.55;
  }, [isXl, containerW, previewZoom]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get<Record<string, unknown>>("/configuration");
      setCfg(normalizeCommercialConfig(data));
      setDraftMode(false);
    } catch {
      setCfg({ ...DEFAULT_COMMERCIAL_CONFIG });
      setDraftMode(true);
      toast.error("Configuration serveur indisponible — mode brouillon.", {
        toastId: "documents-config-get-error",
      });
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  useEffect(() => {
    if (!fullscreenOpen) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key !== "Escape") return;
      e.preventDefault();
      setFullscreenOpen(false);
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [fullscreenOpen]);

  const update =
    (field: keyof CommercialConfig) =>
    (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
      const v = e.target.value;
      setCfg((prev) => (prev ? { ...prev, [field]: v } : prev));
    };

  const updateBool =
    (field: keyof CommercialConfig) =>
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const v = e.target.checked;
      setCfg((prev) => (prev ? { ...prev, [field]: v } : prev));
    };

  const updateInt =
    (field: keyof CommercialConfig) =>
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const raw = e.target.value;
      setCfg((prev) =>
        prev
          ? {
              ...prev,
              [field]: raw === "" ? null : Math.trunc(Number(raw)),
            }
          : prev
      );
    };

  const save = async () => {
    if (!cfg) return;
    setSaving(true);
    try {
      await api.put("/configuration", buildConfigurationPayload(cfg));
      toast.success("Configuration enregistrée.", { toastId: "documents-config-save-ok" });
      setDraftMode(false);
      void load();
    } catch {
      toast.error("Enregistrement impossible.", { toastId: "documents-config-save-error" });
    } finally {
      setSaving(false);
    }
  };

  const previewTitle = useMemo(() => {
    switch (tab) {
      case "facture":
        return "Aperçu modèle — données fictives";
      case "proforma":
        return "Aperçu modèle — données fictives";
      default:
        return "Aperçu modèle — données fictives";
    }
  }, [tab]);

  const documentPreview = useMemo(() => {
    if (!cfg) return null;
    switch (tab) {
      case "facture":
        return <InvoicePreview config={cfg} />;
      case "proforma":
        return <ProformaPreview config={cfg} />;
      default:
        return <QuittancePreview config={cfg} />;
    }
  }, [tab, cfg]);

  if (!cfg) {
    return (
      <div className="flex min-h-[40vh] items-center justify-center text-sm text-slate-400">
        {loading ? "Chargement…" : "Impossible de charger la configuration."}
      </div>
    );
  }

  const modal =
    fullscreenOpen && typeof document !== "undefined"
      ? createPortal(
          <div
            className="fixed inset-0 z-[100] flex min-w-0 flex-col overflow-hidden bg-black/85 p-0"
            role="dialog"
            aria-modal="true"
            aria-labelledby="documents-commercial-fs-title"
          >
            <div className="flex min-h-11 shrink-0 items-center justify-between gap-3 border-b border-slate-800 bg-slate-950/98 px-3 py-3 pl-[max(0.75rem,env(safe-area-inset-left))] pr-[max(0.75rem,env(safe-area-inset-right))] pt-[max(0.75rem,env(safe-area-inset-top))] sm:px-4">
              <h2 id="documents-commercial-fs-title" className="min-w-0 truncate text-sm font-semibold text-white sm:text-base">
                Aperçu plein écran — documents commerciaux
              </h2>
              <button
                type="button"
                onClick={() => setFullscreenOpen(false)}
                className="inline-flex min-h-11 shrink-0 items-center gap-2 rounded-lg border border-slate-600 bg-slate-900 px-3 py-2 text-sm font-medium text-white shadow-lg hover:bg-slate-800"
              >
                <X className="h-4 w-4 shrink-0" aria-hidden />
                Fermer
              </button>
            </div>
            <div className="min-h-0 min-w-0 flex-1 overflow-y-auto overflow-x-hidden px-2 py-4 pb-[max(1rem,env(safe-area-inset-bottom))] sm:px-4">
              <div className="mx-auto w-full max-w-[900px] min-w-0">
                <A4PreviewShell zoom={FULLSCREEN_ZOOM} hideHeader title={previewTitle} marginBottom={32}>
                  {documentPreview}
                </A4PreviewShell>
              </div>
            </div>
          </div>,
          document.body
        )
      : null;

  return (
    <div className="space-y-5">
      {modal}

      <div className="sticky top-0 z-20 -mx-4 flex flex-wrap items-start justify-between gap-4 border-b border-slate-800/80 bg-slate-950/95 px-4 pb-4 pt-1 backdrop-blur-md lg:mx-0 lg:rounded-none lg:border-0 lg:bg-transparent lg:p-0 lg:backdrop-blur-none">
        <div>
          <Link
            to="/admin/parametres"
            className="mb-2 inline-flex items-center gap-2 text-xs font-medium text-slate-400 hover:text-emerald-400"
          >
            <ArrowLeft className="h-3.5 w-3.5" />
            Paramètres
          </Link>
          <div className="flex flex-wrap items-center gap-2">
            <h1 className="text-xl font-semibold tracking-tight text-white">Documents commerciaux</h1>
            {draftMode ? (
              <span className="rounded-full bg-amber-500/15 px-2.5 py-0.5 text-[11px] font-semibold uppercase tracking-wide text-amber-400 ring-1 ring-amber-500/40">
                Mode brouillon
              </span>
            ) : null}
          </div>
          <p className="mt-1 max-w-2xl text-sm text-slate-400">
            Identité, banque, fiscalité, numérotation et mentions sur factures, proformas et quittances.
          </p>
        </div>
        <button
          type="button"
          onClick={() => void save()}
          disabled={saving}
          className="inline-flex shrink-0 items-center gap-2 rounded-xl bg-emerald-600 px-4 py-2.5 text-sm font-semibold text-white shadow-lg shadow-emerald-900/30 hover:bg-emerald-500 disabled:opacity-50"
        >
          <Save className="h-4 w-4" />
          {saving ? "Enregistrement…" : "Enregistrer"}
        </button>
      </div>

      <div className="grid grid-cols-1 gap-6 xl:grid-cols-12">
        <section className="min-w-0 xl:col-span-5">
          <div className="rounded-2xl border border-slate-800 bg-slate-900/80 p-4 shadow-xl ring-1 ring-white/5 xl:max-h-[calc(100vh-8rem)] xl:overflow-y-auto xl:pr-1">
            <h2 className="mb-4 text-sm font-semibold text-slate-200">Configuration</h2>
            <div className="space-y-3">
              <AccordionSection title="Identité" defaultOpen>
                <div className="grid gap-4">
                  <div className="grid gap-4 sm:grid-cols-2">
                    <label className={labelClass}>
                      Nom légal
                      <input value={cfg.nomLegal ?? ""} onChange={update("nomLegal")} className={fieldInput} />
                    </label>
                    <label className={labelClass}>
                      Nom commercial
                      <input
                        value={cfg.nomCommercial ?? ""}
                        onChange={update("nomCommercial")}
                        className={fieldInput}
                      />
                    </label>
                  </div>
                  <label className={labelClass}>
                    Nom entreprise (réf. système)
                    <input
                      value={cfg.nomEntreprise}
                      onChange={update("nomEntreprise")}
                      className={fieldInput}
                      title="Synchronisé avec les anciennes factures ; à l’enregistrement, dérivé si besoin des noms légal/commercial."
                    />
                  </label>
                  <label className={labelClass}>
                    Slogan / mention commerciale
                    <input value={cfg.slogan ?? ""} onChange={update("slogan")} className={fieldInput} />
                  </label>
                  <label className={labelClass}>
                    Adresse complète
                    <textarea
                      value={cfg.adresseComplete ?? ""}
                      onChange={update("adresseComplete")}
                      rows={3}
                      className={fieldInput}
                    />
                  </label>
                  <label className={labelClass}>
                    Adresse (ancien champ)
                    <textarea value={cfg.adresse ?? ""} onChange={update("adresse")} rows={2} className={fieldInput} />
                  </label>
                  <div className="grid gap-4 sm:grid-cols-2">
                    <label className={labelClass}>
                      Téléphone
                      <input value={cfg.telephone ?? ""} onChange={update("telephone")} className={fieldInput} />
                    </label>
                    <label className={labelClass}>
                      E-mail
                      <input type="email" value={cfg.email ?? ""} onChange={update("email")} className={fieldInput} />
                    </label>
                  </div>
                  <label className={labelClass}>
                    Site web
                    <input
                      value={cfg.siteWeb ?? ""}
                      onChange={update("siteWeb")}
                      placeholder="https://…"
                      className={fieldInput}
                    />
                  </label>
                  <label className={labelClass}>
                    Logo (URL)
                    <input value={cfg.logoUrl ?? cfg.logo ?? ""} onChange={update("logoUrl")} className={fieldInput} />
                  </label>
                  <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
                    <label className={labelClass}>
                      RCCM
                      <input value={cfg.rccm ?? ""} onChange={update("rccm")} className={fieldInput} />
                    </label>
                    <label className={labelClass}>
                      NIF
                      <input value={cfg.nif ?? ""} onChange={update("nif")} className={fieldInput} />
                    </label>
                    <label className={labelClass}>
                      ID NAT
                      <input value={cfg.idNat ?? ""} onChange={update("idNat")} className={fieldInput} />
                    </label>
                    <label className={labelClass}>
                      N° impôt TVA
                      <input
                        value={cfg.numeroImpotTva ?? ""}
                        onChange={update("numeroImpotTva")}
                        className={fieldInput}
                      />
                    </label>
                  </div>
                </div>
              </AccordionSection>

              <AccordionSection title="Banque">
                <div className="grid gap-4 sm:grid-cols-2">
                  <label className={labelClass}>
                    Banque
                    <input value={cfg.banque ?? ""} onChange={update("banque")} className={fieldInput} />
                  </label>
                  <label className={labelClass}>
                    Nom du compte
                    <input value={cfg.nomCompte ?? ""} onChange={update("nomCompte")} className={fieldInput} />
                  </label>
                  <label className={labelClass}>
                    N° compte
                    <input value={cfg.numeroCompte ?? ""} onChange={update("numeroCompte")} className={fieldInput} />
                  </label>
                  <label className={labelClass}>
                    IBAN
                    <input value={cfg.iban ?? ""} onChange={update("iban")} className={fieldInput} />
                  </label>
                  <label className={labelClass}>
                    SWIFT
                    <input value={cfg.swift ?? ""} onChange={update("swift")} className={fieldInput} />
                  </label>
                  <label className={labelClass}>
                    Devise du compte
                    <input value={cfg.deviseCompte ?? ""} onChange={update("deviseCompte")} className={fieldInput} />
                  </label>
                  <label className={`${labelClass} sm:col-span-2`}>
                    Mobile money (entreprise)
                    <input
                      value={cfg.mobileMoneyEntreprise ?? ""}
                      onChange={update("mobileMoneyEntreprise")}
                      className={fieldInput}
                    />
                  </label>
                </div>
              </AccordionSection>

              <AccordionSection title="Fiscalité">
                <div className="grid gap-4 sm:grid-cols-2">
                  <label className={labelClass}>
                    Devise par défaut
                    <input value={cfg.deviseDefaut ?? cfg.devise ?? ""} onChange={update("deviseDefaut")} className={fieldInput} />
                  </label>
                  <label className={labelClass}>
                    Taux TVA (%)
                    <input
                      type="number"
                      step="0.01"
                      min={0}
                      value={cfg.tauxTva ?? cfg.tauxTVA ?? ""}
                      onChange={(e) =>
                        setCfg((p) =>
                          p
                            ? {
                                ...p,
                                tauxTVA: e.target.value === "" ? null : Number(e.target.value),
                                tauxTva: e.target.value === "" ? null : Number(e.target.value),
                              }
                            : p
                        )
                      }
                      className={fieldInput}
                    />
                  </label>
                  <label className={`${labelClass} sm:col-span-2`}>
                    Mention TVA
                    <textarea value={cfg.mentionTva ?? ""} onChange={update("mentionTva")} rows={2} className={fieldInput} />
                  </label>
                  <label className="flex cursor-pointer items-center gap-2 text-sm text-slate-300">
                    <input
                      type="checkbox"
                      checked={cfg.exonereTva === true}
                      onChange={updateBool("exonereTva")}
                      className="rounded border-slate-600"
                    />
                    Exonéré TVA
                  </label>
                  <label className={labelClass}>
                    Taux de change
                    <input
                      type="number"
                      step="0.0001"
                      min={0}
                      value={cfg.tauxChange ?? ""}
                      onChange={(e) =>
                        setCfg((p) =>
                          p ? { ...p, tauxChange: e.target.value === "" ? null : Number(e.target.value) } : p
                        )
                      }
                      className={fieldInput}
                    />
                  </label>
                  <label className={labelClass}>
                    Date du taux de change
                    <input
                      type="date"
                      value={cfg.dateTauxChange ?? ""}
                      onChange={update("dateTauxChange")}
                      className={fieldInput}
                    />
                  </label>
                </div>
              </AccordionSection>

              <AccordionSection title="Numérotation" defaultOpen>
                <div className="space-y-4">
                  <label className={labelClass}>
                    Format du numéro (ex. {"{"}PREFIX{"}"}-YYYY-{"{"}SEQ:5{"}"})
                    <input
                      value={cfg.formatNumero ?? DEFAULT_FORMAT_NUMERO}
                      onChange={update("formatNumero")}
                      placeholder="{PREFIX}-YYYY-{SEQ:5}"
                      className={fieldInput}
                    />
                  </label>
                  <div className="grid gap-4 sm:grid-cols-3">
                    <label className={labelClass}>
                      Préfixe facture
                      <input
                        value={cfg.numerotationFacturePrefix ?? ""}
                        onChange={update("numerotationFacturePrefix")}
                        className={fieldInput}
                      />
                    </label>
                    <label className={labelClass}>
                      Préfixe proforma
                      <input
                        value={cfg.numerotationProformaPrefix ?? ""}
                        onChange={update("numerotationProformaPrefix")}
                        className={fieldInput}
                      />
                    </label>
                    <label className={labelClass}>
                      Préfixe quittance
                      <input
                        value={cfg.numerotationQuittancePrefix ?? ""}
                        onChange={update("numerotationQuittancePrefix")}
                        className={fieldInput}
                      />
                    </label>
                  </div>
                  <div className="grid gap-4 sm:grid-cols-3">
                    <label className={labelClass}>
                      Prochain n° facture
                      <input
                        type="number"
                        min={1}
                        value={cfg.prochainNumeroFacture ?? ""}
                        onChange={updateInt("prochainNumeroFacture")}
                        className={fieldInput}
                      />
                    </label>
                    <label className={labelClass}>
                      Prochain n° proforma
                      <input
                        type="number"
                        min={1}
                        value={cfg.prochainNumeroProforma ?? ""}
                        onChange={updateInt("prochainNumeroProforma")}
                        className={fieldInput}
                      />
                    </label>
                    <label className={labelClass}>
                      Prochain n° quittance
                      <input
                        type="number"
                        min={1}
                        value={cfg.prochainNumeroQuittance ?? ""}
                        onChange={updateInt("prochainNumeroQuittance")}
                        className={fieldInput}
                      />
                    </label>
                  </div>
                </div>
              </AccordionSection>

              <AccordionSection title="Mentions">
                <div className="grid gap-4">
                  <label className={labelClass}>
                    Conditions de paiement
                    <textarea
                      value={cfg.conditionsPaiement ?? ""}
                      onChange={update("conditionsPaiement")}
                      rows={2}
                      className={fieldInput}
                    />
                  </label>
                  <div className="grid gap-4 sm:grid-cols-2">
                    <label className={labelClass}>
                      Délai de paiement (jours)
                      <input
                        type="number"
                        min={0}
                        value={cfg.delaiPaiementJours ?? ""}
                        onChange={updateInt("delaiPaiementJours")}
                        className={fieldInput}
                      />
                    </label>
                    <label className={labelClass}>
                      Validité proforma (jours)
                      <input
                        type="number"
                        min={0}
                        value={cfg.validiteProformaJours ?? ""}
                        onChange={updateInt("validiteProformaJours")}
                        className={fieldInput}
                      />
                    </label>
                  </div>
                  <label className={labelClass}>
                    Pénalités de retard
                    <textarea value={cfg.penaliteRetard ?? ""} onChange={update("penaliteRetard")} rows={2} className={fieldInput} />
                  </label>
                  <label className={labelClass}>
                    Clause contestation
                    <textarea
                      value={cfg.clauseContestation ?? ""}
                      onChange={update("clauseContestation")}
                      rows={2}
                      className={fieldInput}
                    />
                  </label>
                  <label className={labelClass}>
                    Pied de page facture
                    <textarea value={cfg.piedPageFacture ?? ""} onChange={update("piedPageFacture")} rows={2} className={fieldInput} />
                  </label>
                  <label className={labelClass}>
                    Mention bas de page — facture (ancien)
                    <textarea value={cfg.mentionFacture ?? ""} onChange={update("mentionFacture")} rows={2} className={fieldInput} />
                  </label>
                  <label className={labelClass}>
                    Mention — proforma
                    <textarea value={cfg.mentionProforma ?? ""} onChange={update("mentionProforma")} rows={2} className={fieldInput} />
                  </label>
                  <label className={labelClass}>
                    Mention — quittance
                    <textarea value={cfg.mentionQuittance ?? ""} onChange={update("mentionQuittance")} rows={2} className={fieldInput} />
                  </label>
                  <label className={labelClass}>
                    Mentions légales
                    <textarea value={cfg.mentionsLegales ?? ""} onChange={update("mentionsLegales")} rows={2} className={fieldInput} />
                  </label>
                </div>
              </AccordionSection>

              <AccordionSection title="Signature">
                <div className="grid gap-4 sm:grid-cols-2">
                  <label className={labelClass}>
                    Nom du signataire
                    <input value={cfg.nomSignataire ?? ""} onChange={update("nomSignataire")} className={fieldInput} />
                  </label>
                  <label className={labelClass}>
                    Fonction
                    <input value={cfg.fonctionSignataire ?? ""} onChange={update("fonctionSignataire")} className={fieldInput} />
                  </label>
                  <label className={`${labelClass} sm:col-span-2`}>
                    Signature / cachet (URL)
                    <input value={cfg.signatureCachetUrl ?? ""} onChange={update("signatureCachetUrl")} className={fieldInput} />
                  </label>
                  <label className={`${labelClass} sm:col-span-2`}>
                    Signature / cachet — ancien champ
                    <input value={cfg.signatureCachet ?? ""} onChange={update("signatureCachet")} className={fieldInput} />
                  </label>
                </div>
              </AccordionSection>
            </div>
          </div>
        </section>

        <section ref={previewWrapRef} className="min-w-0 space-y-4 overflow-x-hidden xl:col-span-7">
          <div className="rounded-xl border border-amber-500/30 bg-amber-500/10 px-4 py-3 text-sm text-amber-100">
            Aperçu modèle — données fictives. Les vraies factures client se consultent depuis le détail de facture.
          </div>
          <div className="flex flex-wrap gap-2">
            {(
              [
                ["facture", "Aperçu facture", FileText],
                ["proforma", "Aperçu proforma", FileText],
                ["quittance", "Aperçu quittance", Receipt],
              ] as const
            ).map(([key, label, Icon]) => (
              <button
                key={key}
                type="button"
                onClick={() => setTab(key)}
                className={[
                  "inline-flex items-center gap-2 rounded-lg px-3 py-2 text-xs font-medium transition",
                  tab === key
                    ? "bg-emerald-600 text-white shadow-md"
                    : "bg-slate-800 text-slate-300 hover:bg-slate-700",
                ].join(" ")}
              >
                <Icon className="h-3.5 w-3.5" />
                {label}
              </button>
            ))}
          </div>

          <div className="flex flex-wrap items-center gap-2 rounded-xl border border-slate-800/80 bg-slate-950/40 px-3 py-2">
            <span className="text-[10px] font-semibold uppercase tracking-wider text-slate-500">Zoom</span>
            {ZOOM_PRESETS.map(({ label, value }) => (
              <button
                key={label}
                type="button"
                onClick={() => setPreviewZoom(value)}
                className={[
                  "rounded-lg px-2.5 py-1 text-[11px] font-medium",
                  Math.abs(previewZoom - value) < 0.02
                    ? "bg-emerald-600 text-white"
                    : "bg-slate-800 text-slate-300 hover:bg-slate-700",
                ].join(" ")}
              >
                {label}
              </button>
            ))}
            <span className="ml-auto text-[10px] text-slate-500">
              {isXl ? (
                <>
                  Affichage <span className="font-mono text-slate-400">{Math.round(effectiveZoom * 100)} %</span>
                </>
              ) : (
                <>Adaptatif · {Math.round(effectiveZoom * 100)} %</>
              )}
            </span>
          </div>

          <A4PreviewShell zoom={effectiveZoom} title={previewTitle} onFullscreen={() => setFullscreenOpen(true)}>
            {documentPreview}
          </A4PreviewShell>
        </section>
      </div>
    </div>
  );
}
